import React from 'react';
import { connect } from 'react-redux';
import useRouter from 'use-react-router';
import { Dispatch } from 'redux';
import { compose } from 'recompose';
import promoterCRUDActions from '@mm/state/actions/PromoterCRUDActions';
import {
  chatbotIsActiveOnConnectedPageSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import { LinkTypes, PromoterModel } from '@mm/state/model/PromoterModel';
import EmptyPageBacksplash from '../../../common/EmptyPageBacksplash/EmptyPageBacksplash';
import { textByType } from '../../build/constants/getPromoterValue';

import htmlElementsImg from 'assets/feature-svgs/html-elements.svg';
import { ITEMS } from '../../../../common/constants/items';
import modalActions, {
  openAddPromoterModal,
} from '@mm/state/modules/modalsState/actions';
import './PromotersPanel.pcss';
import BotPanelHeader from '../../../common/BotPanelHeader/BotPanelHeader';
import { sectionNames } from '../../../sections/sectionNames';
import { RouterComponentProps } from '../../../../common/helpers/routes/interfaces';
import { StoreState } from '@mm/state/store-interfaces';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { PromotersTable } from '../components/PromotersTable/PromotersTable';
import { useCreatePromoter } from './use-create-promoter';
import { usePrimeState, PromotersType } from './use-prime-state';
import { PlusCircleIcon } from '@mm/icons';

type OuterProps = {
  promoterType?: LinkTypes;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = InnerProps & OuterProps;

const mapStateToProps = (state: StoreState) => {
  return {
    bot: activeChatbotSelector(state),
    chatbotIsActiveOnConnectedPage:
      chatbotIsActiveOnConnectedPageSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  createPromoter: (promoter: PromoterModel) =>
    dispatch(promoterCRUDActions.create(promoter)),
  updatePromoter: (botId: number, promoter: PromoterModel) =>
    dispatch(promoterCRUDActions.update(botId, promoter)),
  deletePromoter: (promoter: PromoterModel) =>
    dispatch(promoterCRUDActions.delete(promoter)),
  openAddPromoterModal: (selectPromoter: (type: LinkTypes) => any) =>
    dispatch(openAddPromoterModal(selectPromoter)),
  openActivateInactiveBotModal: () =>
    dispatch(modalActions.openActivateInactiveBotModal()),
  openDeleteConfirmModal: (args: {
    headerText: string;
    deleteCallback: () => void;
  }) => dispatch(modalActions.openDeleteConfirmModal(args)),
});

export const promoterTypeTitleMap: Record<PromotersType, string> = {
  'share-links': 'Share Link',
  'facebook-opt-ins': 'Facebook Opt-in',
  optin: 'Opt-in popup',
};

const getButtonNameEnding = (promotersType: PromotersType) =>
  promoterTypeTitleMap[promotersType];

export const PromotePanel = (props: Props) => {
  const { bot } = props;
  const { createPromoter } = useCreatePromoter();
  const {
    match: { path },
    history,
  } = useRouter();

  const promotersType =
    path === BotBuilderRoutes.shareLinks.path
      ? 'share-links'
      : 'facebook-opt-ins';

  const { promoters, mappedPromoters } = usePrimeState(bot.id, promotersType);

  const onDeletePromoter = (id: number) => {
    const { deletePromoter, openDeleteConfirmModal } = props;
    const promoterToDelete = promoters.find(
      (promoter: PromoterModel) => promoter.id === id,
    );

    if (promoterToDelete) {
      const promoterType = textByType.get(promoterToDelete.type);
      const deleteCallback = () => deletePromoter(promoterToDelete);

      openDeleteConfirmModal({
        headerText: `Are you sure you want to delete this ${promoterType}?`,
        deleteCallback,
      });
    }
  };

  const getBreadcrumbPanelName = () => {
    if (path === BotBuilderRoutes.shareLinks.path) {
      return ITEMS.SHARE_LINKS;
    }

    return ITEMS.HTML_ELEMENTS;
  };

  const onClickAddPromoter = () => {
    const { openAddPromoterModal } = props;

    if (promotersType === 'share-links') return createPromoter('link');
    openAddPromoterModal(createPromoter);
  };

  const onEditPromoter = (
    promoterId: number,
    dialogueId: number,
    typeTitle: string,
  ) => {
    switch (typeTitle) {
      case 'Share Link':
        return history.push(
          BotBuilderRoutes.shareLinkEdit.getLink({
            chatbotId: bot.id,
            promoterId,
            dialogueId,
          }),
        );
      case 'Send to Messenger Button':
        return history.push(
          BotBuilderRoutes.sendToMessengerEdit.getLink({
            chatbotId: bot.id,
            promoterId,
            dialogueId,
          }),
        );
      case 'Checkbox':
        return history.push(
          BotBuilderRoutes.checkboxPluginEdit.getLink({
            chatbotId: bot.id,
            promoterId,
            dialogueId,
          }),
        );
      default:
        throw new Error('Unknown promoter type');
    }
  };

  if (!bot) return null;

  return (
    <div className="promote-panel">
      <BotPanelHeader
        featureGroupName={sectionNames.websiteChat}
        panelName={getBreadcrumbPanelName()}
        rightPanelContent={
          mappedPromoters.length === 0 ? (
            <div />
          ) : (
            <PrimaryButton
              className="promote-panel__button new-entity-button"
              onClick={onClickAddPromoter}
            >
              Add {getButtonNameEnding(promotersType)}
            </PrimaryButton>
          )
        }
      />
      <div className="promote-panel__content-container">
        {mappedPromoters.length === 0 ? (
          <EmptyPageBacksplash pageIcon={htmlElementsImg}>
            <PrimaryButton
              onClick={onClickAddPromoter}
              leftIcon={<PlusCircleIcon />}
            >
              New {getButtonNameEnding(promotersType)}
            </PrimaryButton>
          </EmptyPageBacksplash>
        ) : (
          <div>
            <PromotersTable
              promoters={mappedPromoters}
              edit={onEditPromoter}
              delete={onDeletePromoter}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(PromotePanel);
